// src/generators/dzial9_prawdopodobienstwo/temat1_statystyka_opisowa/podtemat1_srednie_types/MeanArithmeticRawTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function gcd(a, b) { a = Math.abs(a); b = Math.abs(b); while (b) { const t = a % b; a = b; b = t; } return a || 1; }
function simplify(p, q) { if (q < 0) { p = -p; q = -q; } const g = gcd(p, q); return [p / g, q / g]; }
function dfrac(p, q) { const [pp, qq] = simplify(p, q); return qq === 1 ? `${pp}` : `\\dfrac{${pp}}{${qq}}`; }

// ułamek mieszany: np. -7/3 → -2 1/3
function mixedLatex(p, q) {
  let [pp, qq] = simplify(p, q);
  const neg = pp < 0;
  pp = Math.abs(pp);
  const whole = Math.floor(pp / qq);
  const rem = pp % qq;
  if (whole === 0 && rem === 0) return "0";
  if (whole === 0) return (neg ? "-" : "") + `\\dfrac{${rem}}{${qq}}`;
  if (rem === 0) return (neg ? "-" : "") + `${whole}`;
  return (neg ? "-" : "") + `${whole}\\; ${dfrac(rem, qq)}`;
}

// parse: całkowite, a/b, a b/c (mieszany), dziesiętne (kropka/przecinek)
function parseFraction(raw) {
  if (raw == null) return null;
  const s0 = String(raw).trim();
  if (!s0) return null;

  // mixed: "-3 1/2" lub "3 1/2"
  let m = s0.match(/^\s*([+-]?\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const whole = parseInt(m[1], 10);
    const num = parseInt(m[2], 10);
    const den = parseInt(m[3], 10);
    if (den === 0) return null;
    const sign = whole < 0 ? -1 : 1;
    const absWhole = Math.abs(whole);
    const p = sign * (absWhole * den + num);
    return [p, den];
  }

  // improper: "a/b"
  m = s0.match(/^\s*([+-]?\d+)\s*\/\s*([+-]?\d+)\s*$/);
  if (m) {
    const den = parseInt(m[2], 10);
    if (den === 0) return null;
    return [parseInt(m[1], 10), den];
  }

  // integer
  if (/^[+-]?\d+$/.test(s0)) return [parseInt(s0, 10), 1];

  // decimal (comma or dot)
  const s = s0.replace(",", ".");
  if (/^[+-]?(?:\d+|\d*\.\d+)$/.test(s)) {
    const sign = s.startsWith("-") ? -1 : 1;
    const body = s.replace(/^[+-]/, "");
    const [ip, fp = ""] = body.split(".");
    const k = fp.length;
    const p = sign * (parseInt(ip || "0", 10) * (10 ** k) + parseInt(fp || "0", 10));
    const q = 10 ** k;
    return [p, q];
  }
  return null;
}

// ========== ŚREDNIA ARYTMETYCZNA — SUROWE DANE ==========
export function generateTask() {
  const n = 5 + Math.floor(Math.random() * 4); // 5..8 liczb
  const arr = Array.from({ length: n }, () => 5 + Math.floor(Math.random() * 26)); // 5..30
  const sum = arr.reduce((a, b) => a + b, 0);
  const p = sum, q = n;
  const [sp, sq] = simplify(p, q);
  return {
    type: "MeanArithmeticRawTask",
    data: { n, arr, sum, p: sp, q: sq, meanVal: p / q },
    uid: `mean_ar_${Date.now()}_${Math.floor(Math.random() * 1e9)}`
  };
}

export function renderTask({ data }) {
  const arrLatex = data.arr.join(",\\,");
  return (
    <div style={{ textAlign: "left", margin: "8px 0 12px", lineHeight: 1.6 }}>
      <div style={{ marginBottom: 4 }}>
        <span>Podane liczby: </span>
        <InlineMath math={arrLatex} />
        <span>. Oblicz <b>średnią arytmetyczną</b>.</span>
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-3" style={{ marginTop: 4 }}>
      <span><InlineMath math={'\\bar{a} = '} /></span>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        label={"\\bar{a}"}                 // ← zmiana: ładny podgląd „Podgląd: ā = …”
        placeholder="np. 15, 45/3, 3 1/2"
      />
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || !userAnswer.raw) return { correct: false, message: "Podaj ułamek lub liczbę (np. 3 1/2)." };
  const parsed = parseFraction(userAnswer.raw);
  if (!parsed) return { correct: false, message: "Nieprawidłowy zapis (dozw.: 3.5, 7/2, 3 1/2)." };
  const [gp, gq] = parsed;
  const [cp, cq] = [data.p, data.q];

  // 1) równoważny ułamek po uproszczeniu
  let correct = (simplify(gp, gq).join("/") === simplify(cp, cq).join("/"));

  // 2) albo wartość numeryczna z tolerancją ±0.01
  if (!correct) {
    const uv = gp / gq, cv = cp / cq;
    if (Math.abs(uv - cv) <= 0.01 + 1e-12) correct = true;
  }

  const expectedImproper = `\\tfrac{${cp}}{${cq}}`;
  const expectedMixed = mixedLatex(cp, cq);
  return { correct, expected: `${expectedImproper} \\;\\;(=\\; ${expectedMixed})` };
}

export function renderAnswer({ data }) {
  const mixed = mixedLatex(data.p, data.q);
  return <InlineMath math={`\\bar{a} = ${mixed}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  // ← zmiana: matematyczny podgląd z \bar{a}
  return <InlineMath math={formatLatexPreview(userAnswer.raw, '\\bar{a}')} />;
}

export function renderExplanation({ data }) {
  const sumStr = data.arr.join(" + ");
  const improper = dfrac(data.p, data.q);
  const mixed = mixedLatex(data.p, data.q);
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75, marginTop: 8 }}>
      <div><strong>Krok 1 — suma danych</strong></div>
      <BlockMath math={`${sumStr} = ${data.sum}`} />
      <div><strong>Krok 2 — średnia</strong></div>
      <BlockMath math={`\\bar{a} = \\dfrac{${data.sum}}{${data.n}} = ${improper} = ${mixed}`} />
    </div>
  );
}
